import type {
  BrandingSettings,
  GeneralSettings,
  NotificationSettings,
  SecuritySettings,
  SystemSettings,
} from '@/configuration/ConfigurationTypes';
import { appConfig } from '@/config/appConfig';

const supportedLanguages = ['en-US', 'it-IT'] as const;

export const defaultGeneralSettings: GeneralSettings = {
  portalName: appConfig.name,
  organizationName: 'Creditsafe',
  defaultLanguage: 'en-US',
  sessionTimeoutMinutes: 60,
  maintenanceMode: false,
  maintenanceBannerMessage: '',
  portalVersion: '4.12.0',
};

const legacyPortalNameAliases = new Set(['creditsafe enterprise gateway']);

function resolvePortalName(
  portalName: string | undefined,
  fallback: string,
): string {
  const trimmedName = portalName?.trim();

  if (!trimmedName) {
    return fallback;
  }

  if (legacyPortalNameAliases.has(trimmedName.toLowerCase())) {
    return fallback;
  }

  return trimmedName;
}

export function normalizeGeneralSettings(
  general: Partial<GeneralSettings> | undefined,
  defaults: GeneralSettings = defaultGeneralSettings,
): GeneralSettings {
  const candidate = general?.defaultLanguage;
  const defaultLanguage =
    candidate &&
    supportedLanguages.includes(
      candidate as (typeof supportedLanguages)[number],
    )
      ? candidate
      : defaults.defaultLanguage;

  return {
    ...defaults,
    ...general,
    defaultLanguage,
    portalName: resolvePortalName(general?.portalName, defaults.portalName),
    organizationName:
      general?.organizationName?.trim() || defaults.organizationName,
    sessionTimeoutMinutes:
      typeof general?.sessionTimeoutMinutes === 'number'
        ? general.sessionTimeoutMinutes
        : defaults.sessionTimeoutMinutes,
    maintenanceMode: general?.maintenanceMode ?? defaults.maintenanceMode,
    maintenanceBannerMessage:
      general?.maintenanceBannerMessage ?? defaults.maintenanceBannerMessage,
    portalVersion: general?.portalVersion ?? defaults.portalVersion,
  };
}

export function normalizeSystemSettings(
  settings: Partial<SystemSettings> | null | undefined,
  defaults: SystemSettings,
): SystemSettings {
  if (!settings) {
    return defaults;
  }

  return {
    ...defaults,
    ...settings,
    general: normalizeGeneralSettings(settings.general, defaults.general),
    notifications: {
      ...defaults.notifications,
      ...settings.notifications,
    },
    security: {
      ...defaults.security,
      ...settings.security,
    },
    branding: {
      ...defaults.branding,
      ...settings.branding,
    },
    version: settings.version ?? defaults.version,
  };
}

export type {
  GeneralSettings,
  NotificationSettings,
  SecuritySettings,
  BrandingSettings,
};
